import React, { createContext, useContext, useEffect, useState } from "react";
import qz from "qz-tray"; // @TODO lazy load..
import { Barcode2DItem, base64BoxLabelsPdf, base64PalletLabelPdf, mockBase64BoxLabelPdf } from "./pdf/box-label-generation";
import base64FNSKULabelPdf, { FNSKULabelSpec, thirtyUpLabels } from "./pdf/fnsku-label-generation";
import { FbaBatch, FbaBox, FbaShipment, FbaShipmentItem } from "./ShipmentContext";
import { useErrorModalContext } from "./ErrorModalContext";
import { ProductCondition } from "@boxem/core-api-schema";
import PrintFNSKULabelsModal, { FNSKULabelModalItem } from "@/components/PrintFNSKULabelsModal/PrintFNSKULabelsModal";
import { Address } from "./UserContext";
import { LocalStorageKeys } from "@/utils/localStorageUtils";
import { logRocketError } from "@/utils/logUtils";
import { productConditionToString } from "@/app/fba/utils";
import { NotificationType, useNotificationContext } from "./NotificationContext";

export type PrinterContextProps = {
  connectionState: QzConnectionState;
  initiateConnectionToQzTray: () => void,

  submitJobInProgress: boolean;

  printers: PrinterDetails[];

  preferredFNSKULabelPrinter: PrinterDetails | null;
  setPreferredFNSKULabelPrinter: (printer: PrinterDetails | null) => void;
  preferredFNSKULabelSize?: PaperSize | null;
  setPreferredFNSKULabelSize: (size: PaperSize | null) => void;

  fnskuUse30Up: boolean;
  setFnskuUse30Up: (value: boolean) => void;

  preferredBoxLabelPrinter: PrinterDetails | null;
  setPreferredBoxLabelPrinter: (printer: PrinterDetails | null) => void;
  preferredBoxLabelSize?: PaperSize | null;
  setPreferredBoxLabelSize: (size: PaperSize | null) => void;

  useBoxemPrint: boolean;
  setUseBoxemPrint: (value: boolean) => void;

  alwaysDownloadLabels: boolean;
  setAlwaysDownloadLabels: (value: boolean) => void;

  // test functions, prints dummy data:
  printTestFNSKULabel: (download: boolean, print: boolean) => void,
  printTest30UpSheet: (download: boolean, print: boolean) => void;
  printTestBoxLabel: (download: boolean, print: boolean) => void,

  // actually print/download data (in case of failure or 'alwaysDownloadLabels === true', downloads):

  // fnsku labels:
  openPrintFNSKULabelModal: (item: FNSKULabelModalItem) => void;
  printFNSKULabel: (
    fnSku: string,
    productName: string,
    productCondition: ProductCondition,
    copies: number, // how many copies of the label to print (each gets its own page in the pdf)
    expirationDate?: string,
  ) => Promise<void>;

  // box labels:
  printFBABoxLabels(
    shipment: FbaShipment,
    fromAddress: Address,
    boxes: FbaBox[], // in some cases we're only printing a sub-set of the boxes in the shipment, we need this param
    do2DBarcode?: boolean,
    v2Batch?: FbaBatch // @TODO this is actually required
  );

  // pallet labels:
  printPalletLabel: (
    fromAddress: Address,
    toAddress: Address,
    shipmentId: string,
    shipmentName: string,
    shipmentDateCreated: Date,
    copies: number, // how many copies of the label to print (each gets its own page in the pdf)
  ) => Promise<void>;

  // this is for the 'shipping labels' we get back from the SPAPI, which we sometimes need to strip pages 
  // from or otherwise modify before printing.
  handleSPAPIShippingLabelPDF: (
    pdfBlob: Blob,
    boxLabelData?: { // provided if you want box labels too, not just the UPS labels.
      shipment: FbaShipment,
      fromAddress: Address,
      do2DBarcode?: boolean
    },
    v2Batch?: FbaBatch // @TODO remove in v2 and always use the v2 branch
  ) => Promise<void>;

  // print other base64 PDFs:
  printBase64PDF: (options: PrintBase64PDFOptions) => Promise<void>;
};

const PrinterContext = createContext(undefined);
export const usePrinterContext = () => useContext<PrinterContextProps>(PrinterContext);

export interface PaperSize {
  name?: string;
  in?: { width: number, height: number };
  mm?: { width: number, height: number };

  // @HACK, in some cases, we want to display to the user a size for a label that is 'fake', as in, we actually submit
  // a different size to the printer. This is because certain printers will not accept certain label sizes, at least on
  // all platforms, but users want to use labels of that size, and believe they should be able to. This happens with the
  // wired Rollo and 2.25x1.25 labels on mac. It seems like this size, if submitted as-is to the printer, will
  // be ignored, which causes the Rollo to try printing at its default size of 4x6", which produces very bad results. 
  // However, it seems it will accept an input of 2x1". So, we will show some 'fake' options that display the label size the
  // user is looking for, but we will actually submit the 'in' size above when printing.
  fakeIn?: { width: number, height: number };
}

export interface PrinterDetails {
  connection?: string; // like, "USB001", "USB003", etc.
  default?: boolean; // is this printer the system default?
  density?: number; // DPI?
  driver?: string; // name of the driver
  name?: string; // name of the printer, used as an id
  sizes?: PaperSize[];
  trays?: string[];
}

export enum QzConnectionState {
  none, // hasn't tried to connect yet
  connecting,
  connected,
  failed,
  closed, // we were open at one point, but became closed
}

export enum PrinterState {
  none,
  submitPrintJobInProgress,
}

export interface PrintBase64PDFOptions {
  pdfDataBase64: string;
  printerName: string;
  downloadFilename?: string;
  jobName?: string;
  size?: { widthInches: number, heightInches: number };
}

export function guessIfPrinterIsRollo(printer: PrinterDetails): boolean {
  const lowered = printer.driver.toLowerCase();
  return lowered.indexOf("rollo") !== -1;
}

export function guessIfPrinterIsDYMO(printer: PrinterDetails): boolean {
  const lowered = printer.driver.toLowerCase() + printer.name.toLowerCase();
  return lowered.indexOf("dymo") !== -1;
}

// given a base64 encoded pdf, download it in the browser named 'desiredFilename' and open in a new tab.
export function downloadBase64PDF(base64: string, desiredFilename: string) {
  const uri = "data:application/pdf;base64," + base64;
  const a = document.createElement("a");
  a.href = uri;
  a.target = "_blank";
  a.download = desiredFilename;
  a.click();
};

interface PrinterEvent {
  printerName: string;
  type: "RECEIVE" | "ACTION" | "ERROR"; // we ignore "RECEIVE", those appear to be unimportant...

  // unknown if we get these always? docs say we always get a string 'status', but we literally do not...
  statusText: string;
  statusCode: number;

  // these appear to be present for the "ACTION" type:
  eventType?: string; // enum?
  message?: string;
  severity?: string;

  // apparently present for the "ERROR" type: @TODO verify
  exception?: string;

  // job stuff:
  jobId?: number;
  jobName?: string;
}

// key is printer name. stored a single event per printer, most-recent event
let recentPrinterEvents: { [key: string]: PrinterEvent } = {};

export const PrinterContextProvider = (props: { children: React.ReactNode }) => {
  const { showNotification } = useNotificationContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();

  const [connectionState, setConnectionState] = useState<QzConnectionState>(QzConnectionState.none);
  const [printers, setPrinters] = useState<PrinterDetails[]>([]);
  const [useBoxemPrint, setUseBoxemPrint] = useState<boolean>(true);
  const [alwaysDownloadLabels, setAlwaysDownloadLabels] = useState<boolean>(false);
  const [printerState, setPrinterState] = useState<PrinterState>();

  const [preferredFNSKULabelPrinter, setPreferredFNSKULabelPrinter] = useState<PrinterDetails | null>(null);
  const [preferredFNSKULabelSize, setPreferredFNSKULabelSize] = useState<PaperSize | null>(null);
  const [fnskuUse30Up, setFnskuUse30Up] = useState<boolean>(false);

  const [preferredBoxLabelPrinter, setPreferredBoxLabelPrinter] = useState<PrinterDetails | null>(null);
  const [preferredBoxLabelSize, setPreferredBoxLabelSize] = useState<PaperSize | null>(null);

  const [printFNSKUModalItem, setPrintFNSKUModalItem] = useState<FNSKULabelModalItem | null>(null);

  const handleSetFNSKULabelPrinter = (printer: PrinterDetails | null) => {

    if (!printer) {
      window.localStorage.removeItem(LocalStorageKeys.preferredFNSKULabelPrinterName);
      setPreferredFNSKULabelPrinter(null);
      setPreferredFNSKULabelSize(null);
      return;
    }

    window.localStorage.setItem(LocalStorageKeys.preferredFNSKULabelPrinterName, printer.name);

    // @HACK, driver/printer specific hacks:
    if (guessIfPrinterIsRollo(printer)) {

      if (window.navigator.userAgent.indexOf("Mac") != -1) {
        // only on macosx, the 2.25x1.25 label size is not respected (printing 'succeeds' but looks very bad), but the 1x2 is. 
        // However, if you print using size = 1x2 on a 1.25x2.25 label, it looks fine (just a bit small). Because this is a 
        // common enough case, we'll special-case it here, technically lying about the size we will end up submitting to the 
        // printer, but ensuring it works in most cases.
        printer.sizes.push(
          { name: "2x1", in: { width: 2, height: 1 }, fakeIn: { width: 2, height: 1 } },
          { name: "2 1/4 x 1 1/4", in: { width: 1, height: 2 }, fakeIn: { width: 2.25, height: 1.25 } }
        );
      } else {
        printer.sizes.push(
          { name: "2x1", in: { width: 2, height: 1 }, fakeIn: { width: 2, height: 1 } },
          { name: "2 1/4 x 1 1/4", in: { width: 2.25, height: 1.25 }, fakeIn: { width: 2.25, height: 1.25 } }
        );
      }
    } else if (guessIfPrinterIsDYMO(printer)) {
      printer.sizes.push(
        { name: "Aegis 1x2", in: { width: 1, height: 2.1 }, fakeIn: { width: 1, height: 2 } },
        { name: "2 1/4 x 1 1/4", in: { width: 2.25, height: 1.25 } }
      );
    } else {
      // @TODO
      printer.sizes.push(
        { name: "2x1", in: { width: 2, height: 1 }, fakeIn: { width: 2, height: 1 } },
        { name: "1x2", in: { width: 1, height: 2 }, fakeIn: { width: 1, height: 2 } },
      );
    }

    try {
      const preferredLabelSize = window.localStorage.getItem(LocalStorageKeys.preferredFNSKULabelSize);
      if (!!preferredLabelSize) {
        const size = JSON.parse(preferredLabelSize) as PaperSize;
        for (let i = 0; i < printer.sizes.length; i++) {
          if (size.name === printer.sizes[i].name) {
            setPreferredFNSKULabelSize(printer.sizes[i]);
            break;
          }
        }
      }
    } catch (e) {
      // failed to parse json of the preferred label size
      logRocketError(`Error retrieving preferred label size: ${e}`);
    }

    setPreferredFNSKULabelPrinter(printer);
  }

  // try and load preferred printer settings from local storage:
  useEffect(() => {
    const fnskuPrinterName = window.localStorage.getItem(LocalStorageKeys.preferredFNSKULabelPrinterName);
    if (fnskuPrinterName) {
      const fnskuPrinter = printers.find(p => p.name === fnskuPrinterName);
      if (fnskuPrinter) {
        handleSetFNSKULabelPrinter(fnskuPrinter);
      }
    }

    const boxLabelPrinterName = window.localStorage.getItem(LocalStorageKeys.preferredBoxLabelPrinterName);
    if (boxLabelPrinterName) {
      const boxLabelPrinter = printers.find(p => p.name === boxLabelPrinterName);
      if (boxLabelPrinter) {
        setPreferredBoxLabelPrinter(boxLabelPrinter);

        try {
          const preferredLabelSize = window.localStorage.getItem(LocalStorageKeys.preferredBoxLabelSixe);
          if (!!preferredLabelSize) {
            const size = JSON.parse(preferredLabelSize) as PaperSize;
            for (let i = 0; i < boxLabelPrinter.sizes.length; i++) {
              if (size.name === boxLabelPrinter.sizes[i].name) {
                setPreferredBoxLabelSize(boxLabelPrinter.sizes[i]);
                break;
              }
            }
          }
        } catch (e) {
          // failed to parse json of the preferred label size
          logRocketError(`Error retrieving preferred label size: ${e}`);
        }
      }
    }
  }, [printers]);

  // set-up printer event handling
  useEffect(() => {
    const handleEvent = (eventData: PrinterEvent, preferredPrinterName: string) => {
      // note we intentionally do not set recent printer events unconditionally.
      // in certain circumstances you will get offline printers emit statuses 'OFFLINE' -> 'SPOOLING'
      // because we only store the most recent status, we ignore 'spooling', because we want to know
      // if a printer is offline, and we don't care if it's 'SPOOLING'.

      switch (eventData?.statusText) {
        // note that it seems this fires when the printer is out of paper for the 'first' time, after being re-loaded.
        // if you run out of paper, and continue attempting to print, it will not fire this event repeatedly.
        case "PAPER_OUT":
          recentPrinterEvents[preferredPrinterName] = eventData;
          if (useBoxemPrint) {
            showNotification(`${preferredPrinterName} appears to be out of paper!`, NotificationType.ERROR);
          }
          break;

        // happens when the printer is physically disconnected, either at 'run-time' or at start-up:
        case "OFFLINE": {
          const previousEvent = recentPrinterEvents[preferredPrinterName];
          if (!previousEvent || previousEvent.statusText !== "OFFLINE") {
            // avoid spamming offline notifications. It seems we often will get 2 in a row!
            recentPrinterEvents[preferredPrinterName] = eventData;
            if (useBoxemPrint) {
              showNotification(`${preferredPrinterName} appears to be offline! Check the printer is connected and powered on. You may need to turn the printer off and then on again.`, NotificationType.ERROR);
            }
          }
        } break;

        // this fires when 'coming online', but also in a variety of other scenarios, so we can't use it as 'just came online' :(
        // so, we don't want a notification for this case - it's too confusing/fires too much.
        case "OK":
          recentPrinterEvents[preferredPrinterName] = eventData;
          break;

        case "DELETED":
          if (eventData.jobName && useBoxemPrint) {
            showNotification(`${preferredPrinterName} retired job: ${eventData.jobName}`, NotificationType.INFO);
          }
          break;

        case "ERROR":
          if (eventData.jobName && useBoxemPrint) {
            showNotification(`${preferredPrinterName} encountered an error during print job: ${eventData.jobName}`, NotificationType.ERROR);
          }
          break;

        // notes on other statuses:
        //
        // notably, when a print job is initiated (and the printer is phyiscally connected), it seems like we get 'statusText' in the following order (rapidly):
        // 'OK', 'SPOOLING', 'PRINTING', 'RETAINED', 'COMPLETE', 'DELETING', 'DELETED'
        //
        // if instead, the printer is not physically connected, I see a much longer delay (1-3seconds), and then:
        // 'OFFLINE', 'SPOOLING'
        //
        // if a document is manually deleted from the windows printer queue, we get:
        // 'DELETING' -> 'DELETED'
        //
        // if a printer refuses to print/encounters errors doing a job, we get:
        // 'ERROR' -> 'ERROR'
        // where the second 'ERROR' contains the job details, and the first does not.
      }
    };

    if (connectionState === QzConnectionState.connected) {
      qz.printers.setPrinterCallbacks((eventData: PrinterEvent) => {
        // we ignore events for printers that are not our preferred ones.
        if (eventData.printerName === preferredBoxLabelPrinter?.name) {
          handleEvent(eventData, preferredBoxLabelPrinter.name);

        } else if (eventData.printerName === preferredFNSKULabelPrinter?.name) {
          handleEvent(eventData, preferredFNSKULabelPrinter.name);
        }
      });
      qz.printers.stopListening()
        .then(async () => {
          recentPrinterEvents = {};
          await qz.printers.startListening([preferredBoxLabelPrinter?.name, preferredFNSKULabelPrinter?.name]);
          qz.printers.getStatus(); // retrieve status immediately, so we know if our printers are offline.
        });
    }
  }, [preferredBoxLabelPrinter, preferredFNSKULabelPrinter, connectionState]);

  // try and retrieve the user's preference for 'use boxem print', 'always download labels' and 'use 30up labels'.
  useEffect(() => {
    setUseBoxemPrint(!!window.localStorage.getItem(LocalStorageKeys.useBoxemPrint));
    setAlwaysDownloadLabels(!!window.localStorage.getItem(LocalStorageKeys.alwaysDownloadLabels));
    setFnskuUse30Up(!!window.localStorage.getItem(LocalStorageKeys.use30UpLabels));
  }, []);

  // submits a base64 encoded pdf for printing with settings optimal for black/white and barcode printing.
  // returns true if the job was submitted to a printer succesfully, otherwise returns false (false also implies a download for the pdf was done)
  const submitPrintJobPDF = async (
    dataBase64: string,                          // a base64 string, encoding a pdf
    preferredPrinter: PrinterDetails,            // printer to use
    desiredFilename: string = "boxem-label.pdf", // when a printing failure occurs, or if we have 'alwaysDownloadLabels === true', we'll download a pdf with this name
    jobName: string = "Boxem Print Job",         // how you want the job to appear in the user's printer queue
    desiredSizeInches?: { width: number, height: number } // scales to whatever paper size is reported by the printer if not provided
  ): Promise<boolean> => {
    setPrinterState(PrinterState.submitPrintJobInProgress);

    let hasAlreadyDownloadedLabels = false;

    if (alwaysDownloadLabels) {
      downloadBase64PDF(dataBase64, desiredFilename);
      hasAlreadyDownloadedLabels = true;
    }

    const onFailure = () => {
      if (!hasAlreadyDownloadedLabels) { // if the setting is 'alwaysDownloadLabels', on failure we'd download it twice without this!
        downloadBase64PDF(dataBase64, desiredFilename);
      }
      setTimeout(() => setPrinterState(PrinterState.none), 1000); // wait a second to prevent click-spam - this function returns quickly but the printer will take a moment to 'warm up'
      return false;
    };

    if (!useBoxemPrint) {
      console.info("Deciding to not submit print job because 'use Boxem Print' is disabled.");
      return onFailure();
    }

    if (!qz.websocket.isActive()) {
      // @NOTE this check is probably unnecessary, but I suppose could happen if the connection
      // is severed mid-execution?
      console.error("qz connection is not active when trying to submit print job...");
      return onFailure();
    }
    if (!preferredPrinter?.name) {
      console.error("null preferred printer when going to submit a print job...");
      return onFailure();
    }

    // we want to use Boxem Print. first, we should check for a recently stored state of the printer we want to use.
    // this lets us avoid re-submitting print jobs to offline printers, filling up the print queue.
    // @TODO in the future, we may be able to use `qz.printers.clearQueue`, which would be better: https://qz.io/api/qz.printers
    const recentEvent = recentPrinterEvents[preferredPrinter.name];
    if (!!recentEvent && recentEvent.statusText === "OFFLINE") {
      // subtle difference between the notification processed in the 'handleEvent' function:
      // in this circumstance, we open the label in-browser in a new tab - it's easy to miss the notification entirely.
      // we'll persist this one until it is manually closed.
      showNotification(`${preferredPrinter.name} appears to be offline! Check the printer is connected and powered on. Downloading label in your browser...`, NotificationType.ERROR);
      return onFailure();
    }

    // check the printer's status... this appears to be the best way check for errors.
    await qz.printers.getStatus();

    console.info("Submitting a print job. Preferred printer: ", preferredPrinter.name, "desired dimensions: ", desiredSizeInches);

    // try to find the preferred printer:
    let found;
    try {
      found = await qz.printers.find(preferredPrinter.name);

    } catch (e) {
      console.error("failed to find the preferred printer! error:", e);
      return onFailure();
    }

    // we found the preferred printer. try and submit the job:
    const config = qz.configs.create(found, {
      units: "in",
      size: desiredSizeInches ? desiredSizeInches : undefined,
      scaleContent: false,
      margins: 0, // we prefer to encode margins in the pdf itself, so that downloaded pdfs are correct as well.
      jobName: jobName,
      colorType: "grayscale", // we never want color printing. there is also 'blackwhite', but this is supposed to be better for barcodes.
      interpolation: "nearest-neighbor", // according to docs, this minimizes blurriness when doing barcodes - makes sense, you don't want to actually linearly interpolate black/white.
    });
    const printData = [{
      type: "pixel",
      format: "pdf",
      flavor: "base64",
      data: dataBase64,
      ignoreTransparency: true,
      altFontRendering: true,
    }];
    console.info("Found a preferred printer. Printing with config: ", config, "And data: ", printData);

    // await qz.printers.clearQueue({ printerName: preferredFNSKULabelPrinter.name }); // @TODO in release 2.2.4, enable this.

    try {
      await qz.print(config, printData);

    } catch (e) {
      console.error("There was an error submitting a print job: ", e);
      return onFailure();
    }

    setTimeout(() => setPrinterState(PrinterState.none), 1000);  // wait a second to prevent click-spam - this function returns quickly but the printer will take a moment to 'warm up'

    return true;
  };

  const printFNSKULabel = async (
    fnsku: string,
    productName: string,
    productCondition: ProductCondition,
    copies: number,
    expirationDate?: string
  ) => {

    // FNSKUs practically seem to always been 10 characters long, but we technically support the range 0..19, inclusive.
    // validate that:
    if (!fnsku || fnsku.length === 0) {
      logRocketError(`attempting to print a null FNSKU, or an empty string: "${fnsku}"`);
      return;
    }
    if (fnsku.length >= 20) {
      logRocketError(`attempting to print an FNSKU label of 20 or more characters in length: "${fnsku}"`);
      return;
    }

    if (fnskuUse30Up) {
      const labels: FNSKULabelSpec[] = [];
      for (let i = 0; i < copies; i++) {
        labels.push({ fnsku, productName, productCondition, expirationDate })
      }
      const pdfdatab64 = thirtyUpLabels(labels);
      submitPrintJobPDF(
        pdfdatab64,
        preferredFNSKULabelPrinter,
        `boxem-fnsku-label-30up-${fnsku}.pdf`,
        `Boxem Print FNSKU 30-up: ${fnsku}, for "${productName}", ${productCondition}`,
        null
      );

    } else {
      const data = base64FNSKULabelPdf(
        preferredFNSKULabelSize?.in,
        fnsku,
        productName,
        productConditionToString(productCondition),
        copies,
        expirationDate
      );
      submitPrintJobPDF(
        data.data,
        preferredFNSKULabelPrinter,
        `boxem-fnsku-label-${fnsku}.pdf`,
        `Boxem Print FNSKU: ${fnsku}, for "${productName}", ${productCondition}`,
        preferredFNSKULabelSize?.in
      );
    }
  };

  const printPalletLabel = (
    fromAddress: Address,
    toAddress: Address,
    shipmentId: string,
    shipmentName: string,
    shipmentDateCreated: Date,
    copies: number
  ) => {
    const pdfDataBase64 = base64PalletLabelPdf(
      fromAddress,
      toAddress,
      shipmentId,
      shipmentName,
      shipmentDateCreated,
      copies
    );
    return submitPrintJobPDF(
      pdfDataBase64,
      preferredBoxLabelPrinter,
      `boxem-pallet-label-${shipmentName}-${shipmentId}.pdf`,
      `Boxem Print Pallet Label, for ${shipmentId}`,
      preferredBoxLabelSize ? preferredBoxLabelSize.in : undefined,
    );
  }

  const printBoxLabels = (
    fromAddress: Address,
    toAddress: Address,
    shipmentId: string,
    shipmentName: string,
    fulfillmentCentreId: string,
    shipmentDateCreated: Date,
    boxes: {
      boxNumber: number,
      boxContents?: Barcode2DItem[], // provide this if you want a 2D barcode encoding the box contents, otherwise leave empty/undefined
    }[]
  ) => {
    const pdfDataBase64 = base64BoxLabelsPdf(
      fromAddress,
      toAddress,
      shipmentId,
      shipmentName,
      fulfillmentCentreId,
      shipmentDateCreated,
      boxes,
    );
    submitPrintJobPDF(
      pdfDataBase64,
      preferredBoxLabelPrinter,
      `boxem-box-labels-${shipmentName}-${shipmentId}.pdf`,
      `Boxem Print Box Labels, for ${shipmentId}`,
      preferredBoxLabelSize ? preferredBoxLabelSize.in : undefined,
    );
  };

  const handleSPAPIShippingLabelPDF = async (
    pdfBlob: Blob,
    boxLabelData: {
      shipment: FbaShipment,
      fromAddress: Address,
      do2DBarcode: boolean
    } = null,
    v2Batch: FbaBatch = null,
  ) => {
    import("pdf-lib").then(async pdflib => {
      let pdfDocument;
      try {
        pdfDocument = await pdflib.PDFDocument.load(await pdfBlob.arrayBuffer());

      } catch (errors: any) {
        if (handleErrorResponseGenerically(errors)) {
          return;
        }
      }

      // if the user has the setting for their batch as 'print box labels step 3',
      // then they get into a state in step 4 where they want to retrieve the UPS labels,
      // but not the box labels (because those were likely previously printed in step 3)

      // The SPAPI doesn't provide us a way to do this, instead giving us a pdf with pages
      // that alternate between the box labels and the UPS labels, one pair for each box.

      // here we use a third-party library to strip the pages we don't want. 
      // this was investigated to be done without a third-party library,
      // but it would've taken at least 1-2 days to complete in that manner, so we're just
      // doing this for now. This is the only reason we use the PDF library.

      // if the user DOES want the box labels, moreover, the SPAPI is finnicky to get 
      // box contents 2D barcodes to appear - I haven't managed it. so in that case,
      // we still strip the pages, but replace them with our own box labels.
      const numPages = pdfDocument.getPageCount();
      for (let i = numPages - 1; i >= 0; i--) {
        if ((i & 0b1) === 0) {
          pdfDocument.removePage(i);
        }
      }

      const boxes = [];
      if (!!boxLabelData) {
        // we want to insert the pages for the box labels at page indices 0, 2, 4, etc. up to however many boxes there are.
        if (boxLabelData.do2DBarcode) {
          boxLabelData.shipment.boxes.forEach(box => {
            let boxContents = [];
            if (v2Batch) {
              box = v2Batch.boxes.find(b => b.id === box.id); // @HACK, the box on the shipment doesn't have items for some reason!!!
              boxContents = box.packingItems.map(item => {
                const batchItem = v2Batch.items.find(i => i.id === item.batchItemId);
                const packingGroup = v2Batch.chosenPackingOption.packingGroups.find(pg => pg.inboundPackingGroupId === box.packingGroupId);
              
                const packingItem = packingGroup.items.find(i => i.msku === batchItem.sku);
                return {
                  fnsku: packingItem.fnsku,
                  quantity: item.quantity,
                  expirationDate: batchItem.expirationDate,
                }
              });
            } else {
              box.items.forEach(item => {
                const shipmentItem: FbaShipmentItem = boxLabelData.shipment.items.find(_item => _item.id === item.shipmentItemId);
                boxContents.push({
                  fnsku: shipmentItem.batchItem.fnSku,
                  quantity: item.quantity,
                  expirationDate: shipmentItem.batchItem.expirationDate
                });
              })
            }
            
            boxes.push({
              boxNumber: box.displayIndex,
              boxContents: boxContents,
            });
          })
        } else {
          // we want the box labels but no contents (no 2d barcodes, etc)
          boxes.push(...boxLabelData.shipment.boxes.map(box => ({ boxNumber: box.displayIndex })));
        }

        const boxLabelsBase64 = base64BoxLabelsPdf(
          boxLabelData.fromAddress,
          boxLabelData.shipment.destinationAddress,
          boxLabelData.shipment.shipmentId,
          boxLabelData.shipment.shipmentName,
          boxLabelData.shipment.fulfillmentCenterId,
          boxLabelData.shipment.createdAt,
          boxes
        );
        const boxLabelsPdf = await pdflib.PDFDocument.load(boxLabelsBase64);
        const copiedPages = await pdfDocument.copyPages(boxLabelsPdf, boxLabelsPdf.getPageIndices());
        for (let i = 0; i < copiedPages.length; i++) {
          pdfDocument.insertPage(i * 2, copiedPages[i]);
        }
      }

      // submit the print job
      const b64 = await pdfDocument.saveAsBase64();
      await submitPrintJobPDF(
        b64,
        preferredBoxLabelPrinter,
        `boxem-shipping-labels.pdf`,
        `Boxem Print Shipping Labels`,
        preferredBoxLabelSize ? preferredBoxLabelSize.in : undefined,
      );
    });
  };

  const printFBABoxLabels = (
    shipment: FbaShipment,
    fromAddress: Address,
    boxes: FbaBox[], // in some cases we're only printing a sub-set of the boxes in the shipment, we need this param
    do2DBarcode: boolean = false,
    v2Batch: FbaBatch,
  ) => {
    const toAddress = shipment.destinationAddress;

    const _boxes = boxes.map(box => {
      const b = { boxNumber: box.displayIndex, boxContents: [] };
      if (do2DBarcode) {
        // we need to provide box contents and encode that in the box label as well:
        b.boxContents = box.packingItems.map(item => {
          const batchItem = v2Batch.items.find(i => i.id === item.batchItemId);
          const packingGroup = v2Batch.chosenPackingOption.packingGroups.find(pg => pg.inboundPackingGroupId === box.packingGroupId);
          const packingItem = packingGroup.items.find(i => i.msku === batchItem.sku);
          return {
            fnsku: packingItem.fnsku,
            quantity: item.quantity,
            expirationDate: batchItem.expirationDate,
          }
        });
      }
      return b;
    });

    printBoxLabels(
      fromAddress,
      toAddress,
      shipment.shipmentId,
      shipment.shipmentName,
      shipment.fulfillmentCenterId,
      shipment.createdAt,
      _boxes
    );
  };

  const initiateConnectionToQzTray = () => {
    if (connectionState === QzConnectionState.connected) {
      console.info("Already connected to Boxem Print, doing nothing....");
      return;
    }

    if (connectionState === QzConnectionState.connecting) {
      console.info("Already trying to connect to Boxem Print, doing nothing...");
      return;
    }

    setConnectionState(QzConnectionState.connecting);
    qz.security.setCertificatePromise(new Promise((resolve, reject) => {
      fetch("/txt/digital-certificate.txt", { cache: "no-store", headers: { "Content-Type": "text/plain" } })
        .then(data => data.ok ? resolve(data.text()) : reject(data.text()))
        .then(() => {

          qz.websocket.setErrorCallbacks(event => {
            // @TODO only known case this happens if there was an error in initially connecting to the websocket host.
            // we do not get this fired when printing fails, for example.
            console.error("QZ ERROR EVENT:", event);
          });

          qz.websocket.setClosedCallbacks((event: CloseEvent) => {
            console.info("QZ CLOSED CALLBACK:", event);
            // we get called here if the websocket connection terminates... @TODO open modal or something.
            // this happens if user closes Boxem Print while the app is open.
            setConnectionState(QzConnectionState.closed);
          });

          qz.websocket.connect({ usingSecure: true })
            .then(async () => {
              // qz.api.showDebug(true); // show extra debug messages, @TODO disable in production

              // set the signature method - when qz goes to print or retrieve data about printers etc.
              // how does it know the connection can be trusted? this endpoint provides that trust.
              qz.security.setSignatureAlgorithm("SHA512");
              qz.security.setSignaturePromise((toSign) => {
                return function (resolveSignature, _) {
                  fetch(`${process.env.PUBLIC_API_URL}/auth/signature/` + toSign, { credentials: "include", headers: { "Content-Type": "text/plain" } })
                    .then(response => resolveSignature(response.text()));
                };
              });

              const apiVersion: string = await qz.api.getVersion();
              console.info("Boxem Print detected version: ", apiVersion, ", Boxem Print frontend lib version:", qz.version);
              if (apiVersion !== qz.version) {
                // console.warn(`qz version and api version do not match! api (server): '${apiVersion}', qz (client): '${qz.version}'`);
              }

              const printerDetails = await qz.printers.details();
              setPrinters(printerDetails);

              setConnectionState(QzConnectionState.connected);
            })
            .catch(e => {
              console.info("Failed to connect to Boxem Print: ", e);
              setConnectionState(QzConnectionState.failed);
            });
        })
    }));
  };

  const printTestFNSKULabel = async (download: boolean = false, print: boolean = true) => {
    const data = base64FNSKULabelPdf(
      preferredFNSKULabelSize?.in,
      "X262UXDNCZ",
      "Lego Amelia Earheart Tribute 40450 Exclusive Building Set",
      "Used - Acceptable",
      1,
      new Date().toString()
    );
    let success;
    if (print) {
      success = await submitPrintJobPDF(
        data.data,
        preferredFNSKULabelPrinter,
        `boxem-test-fnsku-label.pdf`,
        `Boxem Print Job - test FNSKU label`,
        preferredFNSKULabelSize?.in
      );
    }
    // the reason this isn't just 'if download', is because we could've downloaded the label already
    // as a result of printing if we failed to print, or if 'alwaysDownloadLabels' is true.
    if (download && (!print || (success && !alwaysDownloadLabels))) {
      downloadBase64PDF(data.data, `boxem-test-fnsku-label.pdf`);
    }
  };

  const printTestBoxLabel = async (download: boolean = false, print: boolean = true) => {
    const base64 = mockBase64BoxLabelPdf();
    let success;
    if (print) {
      success = await submitPrintJobPDF(
        base64,
        preferredBoxLabelPrinter,
        `boxem-test-box-label.pdf`,
        `Boxem Print Job - test box label`,
        preferredBoxLabelSize?.in
      );
    }
    // the reason this isn't just 'if download', is because we could've downloaded the label already
    // as a result of printing if we failed to print, or if 'alwaysDownloadLabels' is true.
    if (download && (!print || (success && !alwaysDownloadLabels))) {
      downloadBase64PDF(base64, `boxem-test-box-label.pdf`);
    }
  };

  const printTest30UpSheet = async (download: boolean, print: boolean) => {

    const testLabels: FNSKULabelSpec[] = [];
    for (let i = 0; i < 30; i++) {
      testLabels.push({
        fnsku: "X262UXDNCZ",
        productName: "Lego Amelia Earheart Tribute 40450 Exclusive Building Set",
        productCondition: "Used - Acceptable",
        expirationDate: new Date().toString()
      });
    }

    const base64 = thirtyUpLabels(testLabels);
    let success;

    if (print) {
      success = await submitPrintJobPDF(
        base64,
        preferredFNSKULabelPrinter,
        `boxem-test-30up-sheet.pdf`,
        `Boxem Print Job - test 30up sheet`,
      );
    }
    // the reason this isn't just 'if download', is because we could've downloaded the label already
    // as a result of printing if we failed to print, or if 'alwaysDownloadLabels' is true.
    if (download && (!print || (success && !alwaysDownloadLabels))) {
      downloadBase64PDF(base64, `boxem-test-30up-sheet.pdf`);
    }
  };

  const _setUseBoxemPrint = (value: boolean) => {
    if (value) {
      window.localStorage.setItem(LocalStorageKeys.useBoxemPrint, LocalStorageKeys.useBoxemPrint);
    } else {
      window.localStorage.removeItem(LocalStorageKeys.useBoxemPrint);

      // turning use boxem print off has the side-effect of turning 'always download labels' on:
      _setAlwaysDownloadLabels(true);
    }
    setUseBoxemPrint(value);
  };

  const _setAlwaysDownloadLabels = (value: boolean) => {
    if (value) {
      window.localStorage.setItem(LocalStorageKeys.alwaysDownloadLabels, LocalStorageKeys.alwaysDownloadLabels);
    } else {
      window.localStorage.removeItem(LocalStorageKeys.alwaysDownloadLabels);
    }
    setAlwaysDownloadLabels(value);
  };

  // prints any PDF encoded as base64 (unlike the other functions above
  // which are for printing specific pdfs which implicitly specific printers, etc.)
  // we use this one for misc pdfs, like Suffocation Stickers, Fragile Warnings, etc.
  const printBase64PDF = async (options: PrintBase64PDFOptions) => {
    const printer = printers.find(p => p.name === options.printerName);
    if (!printer) {
      downloadBase64PDF(options.pdfDataBase64, options.downloadFilename ?? `boxem-label.pdf`);
      return;
    }
    await submitPrintJobPDF(
      options.pdfDataBase64,
      printer,
      options.downloadFilename ?? `boxem-label.pdf`,
      options.jobName ?? "Boxem Print Job",
      options.size ? { width: options.size.widthInches, height: options.size.heightInches } : undefined,
    );
  }

  return (
    <PrinterContext.Provider value={{
      connectionState,
      initiateConnectionToQzTray,

      submitJobInProgress: printerState === PrinterState.submitPrintJobInProgress,

      printers,

      preferredFNSKULabelPrinter,
      setPreferredFNSKULabelPrinter: (printer: PrinterDetails | null) => {
        handleSetFNSKULabelPrinter(printer);
      },
      preferredFNSKULabelSize,
      setPreferredFNSKULabelSize: (size: PaperSize | null) => {
        if (size) {
          window.localStorage.setItem(LocalStorageKeys.preferredFNSKULabelSize, JSON.stringify(size));
        } else {
          window.localStorage.removeItem(LocalStorageKeys.preferredFNSKULabelSize);
        }
        setPreferredFNSKULabelSize(size);
      },

      fnskuUse30Up,
      setFnskuUse30Up: (value: boolean) => {
        if (value) {
          window.localStorage.setItem(LocalStorageKeys.use30UpLabels, LocalStorageKeys.use30UpLabels);
        } else {
          window.localStorage.removeItem(LocalStorageKeys.use30UpLabels);
        }
        setFnskuUse30Up(value);
      },

      preferredBoxLabelPrinter,
      setPreferredBoxLabelPrinter: (printer: PrinterDetails | null) => {
        if (printer) {
          window.localStorage.setItem(LocalStorageKeys.preferredBoxLabelPrinterName, printer.name);
        } else {
          window.localStorage.removeItem(LocalStorageKeys.preferredBoxLabelPrinterName);
        }
        setPreferredBoxLabelPrinter(printer);
      },
      preferredBoxLabelSize,
      setPreferredBoxLabelSize: (size: PaperSize | null) => {
        if (size) {
          window.localStorage.setItem(LocalStorageKeys.preferredBoxLabelSixe, JSON.stringify(size));
        } else {
          window.localStorage.removeItem(LocalStorageKeys.preferredBoxLabelSixe);
        }
        setPreferredBoxLabelSize(size);
      },

      useBoxemPrint,
      setUseBoxemPrint: _setUseBoxemPrint,

      alwaysDownloadLabels,
      setAlwaysDownloadLabels: _setAlwaysDownloadLabels,

      // test functions, prints and/or downloads dummy data:
      printTestFNSKULabel: (download: boolean = false, print: boolean = true) => {
        printTestFNSKULabel(download, print);
      },
      printTestBoxLabel: (download: boolean = false, print: boolean = true) => {
        printTestBoxLabel(download, print)
      },
      printTest30UpSheet: (download: boolean = false, print: boolean = true) => {
        printTest30UpSheet(download, print);
      },

      // actually print data (or upon failure, download a pdf label):
      openPrintFNSKULabelModal: (item: FNSKULabelModalItem) => {
        setPrintFNSKUModalItem(item);
      },
      printFNSKULabel,
      printFBABoxLabels,
      printPalletLabel,
      printBase64PDF,
      handleSPAPIShippingLabelPDF,
    }}>
      <PrintFNSKULabelsModal
        item={printFNSKUModalItem}
        onClose={() => setPrintFNSKUModalItem(null)}
      />
      {props.children}
    </PrinterContext.Provider>
  )
}
